import { useState } from "react";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

import { Trash2 } from "lucide-react";

const PaintEditSheet = ({ open, onOpenChange, item, onSave, onDelete }) => {
    const [formData, setFormData] = useState(item);

    // Update form when item changes
    if (item && formData?.name !== item.name) {
        setFormData(item);
    }

    const handleSave = () => {
        if (formData) {
            onSave(formData);
            toast.success("Paint details updated");
            onOpenChange(false);
        }
    };

    const handleDelete = () => {
        if (onDelete && window.confirm(`Are you sure you want to delete the paint record for ${formData.name}?`)) {
            onDelete(formData.id);
            onOpenChange(false);
        }
    };

    if (!formData) return null;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="bottom"
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="max-h-[85vh] rounded-t-3xl flex flex-col"
                style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
            >
                <SheetHeader className="pb-4 flex-shrink-0">
                    <SheetTitle className="text-foreground">Edit Paint Details</SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 space-y-4 pb-4">
                    <div className="space-y-2">
                        <Label htmlFor="room" className="text-muted-foreground">Room</Label>
                        <Input
                            id="room"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="bg-background/50 border-border/50 rounded-xl"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="brand" className="text-muted-foreground">Brand</Label>
                        <Input
                            id="brand"
                            value={formData.brand}
                            onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                            placeholder="e.g., Plascon, Dulux"
                            className="bg-background/50 border-border/50 rounded-xl"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="code" className="text-muted-foreground">Paint Code/Color</Label>
                        <Input
                            id="code"
                            value={formData.code}
                            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                            placeholder="e.g., Evening Mist"
                            className="bg-background/50 border-border/50 rounded-xl"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="finish" className="text-muted-foreground">Finish</Label>
                        <Input
                            id="finish"
                            value={formData.finish}
                            onChange={(e) => setFormData({ ...formData, finish: e.target.value })}
                            placeholder="e.g., Suede, Semi-gloss, Matte"
                            className="bg-background/50 border-border/50 rounded-xl"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="lastPainted" className="text-muted-foreground">Last Painted Date</Label>
                        <Input
                            id="lastPainted"
                            type="date"
                            value={formData.lastPainted || ""}
                            onChange={(e) => setFormData({ ...formData, lastPainted: e.target.value })}
                            className="bg-background/50 border-border/50 rounded-xl w-full"
                        />
                    </div>
                </div>

                <div className="flex-shrink-0 pt-2 space-y-3">
                    <Button onClick={handleSave} className="w-full rounded-xl">
                        Save Changes
                    </Button>
                    <Button
                        variant="ghost"
                        onClick={handleDelete}
                        className="w-full text-destructive hover:text-destructive hover:bg-destructive/10 rounded-xl"
                    >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Paint Record
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default PaintEditSheet;
